import { STAGES } from "../../../utils/stageManagement";

export const navItems = [
  {
    id: "general",
    name: "General Information",
    shortName: "General Info",
    icon: "fi fi-rr-info",
    stage: STAGES.GENERAL,
    description: "Basic package details and destination information",
    essential: true,
  },
  {
    id: "itinerary",
    name: "Package Itinerary",
    shortName: "Itinerary",
    icon: "fi fi-rr-map",
    stage: STAGES.ITINERARY,
    description: "Day-wise plan, meals, attractions, and travel details",
    essential: true,
  },
  {
    id: "stay",
    name: "Stay Information",
    shortName: "Stay",
    icon: "fi fi-rr-home",
    stage: STAGES.STAY,
    description: "Hotels, room categories, and accommodation options",
    essential: true,
  },
  {
    id: "inclusion",
    name: "Inclusion and Exclusion",
    shortName: "Inclusion & Exclusion",
    icon: "fi fi-rr-list-check",
    stage: STAGES.INCLUSION,
    description: "What's included, excluded, and things to carry",
    essential: true,
  },
  {
    id: "pricing",
    name: "Pricing",
    shortName: "Pricing",
    icon: "fi fi-rr-money-bill-wave",
    stage: STAGES.PRICING,
    description: "Rates, discounts, payment terms, and stop sales",
    essential: true,
  },
  {
    id: "additional",
    name: "Additional Information",
    shortName: "Additional Info",
    icon: "fi fi-rr-document",
    stage: STAGES.ADDITIONAL,
    description: "Extra notes and information for travellers",
    essential: false,
    defaultCollapsed: true,
  },
  {
    id: "terms",
    name: "Terms & Conditions",
    shortName: "Terms",
    icon: "fi fi-rr-memo-circle-check",
    stage: STAGES.TERMS,
    description: "Booking rules, policies, and legal terms",
    essential: false,
    defaultCollapsed: true,
  },
  {
    id: "cancellation",
    name: "Cancellation Policy",
    shortName: "Cancellation",
    icon: "fi fi-rr-cross-circle",
    stage: STAGES.CANCELLATION,
    description: "Refund rules and cancellation timelines",
    essential: false,
    defaultCollapsed: true,
  },
  {
    id: "faq",
    name: "FAQs",
    shortName: "FAQ",
    icon: "fi fi-rr-interrogation",
    stage: STAGES.FAQ,
    description: "Common questions and answers about the package",
    essential: false,
  },
];

export const isSectionCompleted = (completedStage, itemStage) => {
  const stage = Number(completedStage) || 0;
  return stage > itemStage || (stage === STAGES.FAQ && itemStage === STAGES.FAQ);
};

export const getNextSectionId = (currentSectionId) => {
  const index = navItems.findIndex((item) => item.id === currentSectionId);
  if (index === -1 || index >= navItems.length - 1) return null;
  return navItems[index + 1].id;
};

export const isOptionalPackageSection = (sectionId) => {
  const item = navItems.find((nav) => nav.id === sectionId);
  return Boolean(item && !item.essential);
};

export const getCompletedSectionsCount = (completedStage) => {
  const stage = Number(completedStage) || 0;
  if (stage >= STAGES.FAQ) return navItems.length;
  return navItems.filter((item) => stage > item.stage).length;
};

export const getCompletionPercent = (completedStage) => {
  const completed = getCompletedSectionsCount(completedStage);
  return Math.round((completed / navItems.length) * 100);
};

export const getMissingSections = (completedStage) => {
  const stage = Number(completedStage) || 0;
  if (stage >= STAGES.FAQ) return [];
  return navItems
    .filter((item) => !isSectionCompleted(stage, item.stage))
    .map((item) => item.name);
};

export const WIZARD_STEPS = [
  {
    id: "basic",
    title: "Basic Information",
    shortTitle: "Basic Info",
    description:
      "Package name, type, destination, duration, category, and cover image",
    icon: "fi fi-rr-info",
    sectionIds: ["general"],
    maxStage: STAGES.GENERAL,
  },
  {
    id: "itinerary",
    title: "Itinerary Builder",
    shortTitle: "Itinerary",
    description: "Day-by-day plan with activities, meals, and accommodations",
    icon: "fi fi-rr-map",
    sectionIds: ["itinerary"],
    maxStage: STAGES.ITINERARY,
  },
  {
    id: "stay",
    title: "Accommodation & Stay",
    shortTitle: "Stay",
    description: "Hotels, room types, stay details, and hotel gallery",
    icon: "fi fi-rr-home",
    sectionIds: ["stay"],
    maxStage: STAGES.STAY,
  },
  {
    id: "inclusions",
    title: "Inclusion and Exclusion",
    shortTitle: "Inclusion & Exclusion",
    description: "What's included, excluded, and things to carry",
    icon: "fi fi-rr-list-check",
    sectionIds: ["inclusion"],
    maxStage: STAGES.INCLUSION,
  },
  {
    id: "pricing",
    title: "Pricing",
    shortTitle: "Pricing",
    description: "Base price, discounts, payment terms, and taxes",
    icon: "fi fi-rr-money-bill-wave",
    sectionIds: ["pricing"],
    maxStage: STAGES.PRICING,
  },
  {
    id: "policies",
    title: "Policies",
    shortTitle: "Policies",
    description: "Additional notes, terms & conditions, and cancellation",
    icon: "fi fi-rr-memo-circle-check",
    sectionIds: ["additional", "terms", "cancellation"],
    maxStage: STAGES.CANCELLATION,
    subSections: [
      { id: "additional", title: "Additional Information" },
      { id: "terms", title: "Terms & Conditions" },
      { id: "cancellation", title: "Cancellation Policy" },
    ],
  },
  {
    id: "faq",
    title: "SEO & Publishing",
    shortTitle: "FAQ",
    description: "FAQs, meta content, and publishing options",
    icon: "fi fi-rr-interrogation",
    sectionIds: ["faq"],
    maxStage: STAGES.FAQ,
  },
  {
    id: "review",
    title: "Review & Publish",
    shortTitle: "Review",
    description: "Preview your package before publishing",
    icon: "fi fi-rr-eye",
    sectionIds: [],
    isReview: true,
  },
];

export const getStepIndexFromStage = (stage) => {
  const completedStage = Number(stage) || 0;
  if (completedStage >= STAGES.FAQ) {
    return WIZARD_STEPS.length - 1;
  }

  for (let i = 0; i < WIZARD_STEPS.length; i += 1) {
    const step = WIZARD_STEPS[i];
    if (step.isReview) continue;
    if (completedStage < step.maxStage) {
      return i;
    }
  }

  return WIZARD_STEPS.length - 2;
};

export const getMaxAccessibleStepIndex = (completedStage, hasPackageId) => {
  if (!hasPackageId) return 0;

  const stage = Number(completedStage) || 0;
  if (stage >= STAGES.FAQ) {
    return WIZARD_STEPS.length - 1;
  }

  let maxIndex = 0;
  WIZARD_STEPS.forEach((step, index) => {
    if (step.isReview) return;
    if (stage >= step.maxStage) {
      maxIndex = index + 1;
    } else if (index === 0 || stage >= step.maxStage - 1) {
      maxIndex = Math.max(maxIndex, index);
    }
  });

  return Math.min(maxIndex, WIZARD_STEPS.length - 1);
};

export const getWizardCompletionPercent = (completedStage) => {
  const stage = Number(completedStage) || 0;
  const contentSteps = WIZARD_STEPS.filter((step) => !step.isReview);
  const completed = contentSteps.filter((step) => stage >= step.maxStage).length;
  return Math.round((completed / contentSteps.length) * 100);
};
